import type { CallView, PhoneSlot } from "@gioia365/contracts";
import type { ApiRequestError } from "../api/http";
import type { TabContext } from "../session/tab-context";
import type { BrowserPhoneError } from "./browser-phone";
import type { OutcomeInput } from "./telephony-queries";

// Gli stati e l'interfaccia della chiamata della scheda del browser (piano M4, Task 13): le fasi sono spiegate in
// `call-session.tsx`, che esporta tutto ciò che è qui e da cui il Power Dialer (M5) li importa.

/**
 * La diagnosi del numero da proporre nella postazione (TEL-10: 21217 «Numero non valido», 13224 «Non raggiungibile»): soltanto su
 * una chiamata finita che non ha contato e non ha un esito. Una chiamata valida ha l'esito obbligatorio (`needsOutcome`, TEL-18) e
 * non lo nasconde dietro la diagnosi; il server la toglie già, e qui non si ripropone.
 */
export function numberDiagnosisOf(call: Pick<CallView, "quality" | "needsOutcome" | "outcome"> | null): NonNullable<NonNullable<CallView["quality"]>["numberProblem"]> | null {
  if (call === null || call.needsOutcome || call.outcome !== null) return null;
  return call.quality?.numberProblem ?? null;
}

/** Il posto chiamato, nella scheda in cui la chiamata è nata, e la sessione del Power Dialer da cui parte (M5). */
export type CallTarget = { tab: TabContext; leadId: string; phone: PhoneSlot | null; dialerSessionId: string | null };

/** Il salvataggio dell'esito: niente ancora, in corso (`retrying` dopo un guasto passeggero), rifiutato, confermato. */
export type OutcomeSave =
  | { state: "none" }
  | { state: "saving"; input: OutcomeInput; retrying: boolean }
  | { state: "failed"; input: OutcomeInput; error: unknown; requestId: string }
  /** `late`: la chiamata aveva già un esito (`409 call_outcome_exists`): resta quello, e la scelta nuova è nella Timeline. */
  | { state: "saved"; call: CallView; late: boolean };

/**
 * Perché una chiamata non è partita: il telefono del browser, il rifiuto del server, oppure (`not_connected`) la chiamata
 * finita senza arrivare al Lead, di cui il server non accetta l'esito (`409 invalid_state`). `call_gone` è la chiamata finita
 * che il server non trova più quando arriva l'esito (`404 call_not_found`: il Lead eliminato per sempre nel frattempo).
 */
export type CallSessionError = BrowserPhoneError | ApiRequestError | { kind: "not_connected" } | { kind: "call_gone" }
  | { kind: "closed_early" } | { kind: "hangup_unmeasured" } | { kind: "closure_unconfirmed" };

export type CallSession =
  | { phase: "idle" }
  | { phase: "starting"; target: CallTarget; leadName: string; call: CallView | null }
  | { phase: "ringing" | "live"; target: CallTarget; leadName: string; call: CallView; startedAt: number }
  | { phase: "ending"; target: CallTarget; leadName: string; call: CallView }
  | {
      phase: "outcome";
      target: CallTarget;
      leadName: string;
      call: CallView;
      missing: boolean;
      save: OutcomeSave;
      notice: BrowserPhoneError | null;
      /**
       * La conversazione misurata dal browser, in secondi, dalla risposta del Lead alla fine; `null` se il browser non l'ha
       * vista (nessuna risposta, o la chiamata ripresa con «Manca l'esito…»). Vale finché il server non dà la sua durata.
       */
      talkSeconds: number | null;
      /**
       * Nessuno ha risposto (il browser non ha mai visto la risposta del Lead: con `answerOnBridge` l'`accept` del SDK arriva
       * soltanto allora) e chi chiama non l'ha chiusa con «Chiudi la chiamata»: squillo a vuoto, occupato, rifiutata, spento. Il
       * Power Dialer aspetta allora l'esito di Gioia365 (TEL-32). Falso per una chiamata ripresa con «Manca l'esito…».
       */
      unanswered: boolean;
    }
  | { phase: "error"; target: CallTarget | null; leadName: string | null; call: CallView | null; error: CallSessionError };

/** Le fasi della chiamata in corso, da «Chiama» premuto fino alla chiusura. */
export type OngoingSession = Extract<CallSession, { phase: "starting" | "ringing" | "live" }>;
export type OutcomeOrigin = OngoingSession | Extract<CallSession, { phase: "ending" }>;

/**
 * L'ingresso di `start`. `dialerSessionId` va nel corpo di `POST /api/leads/:leadId/calls` soltanto quando c'è (M5).
 * `leadName` è il nome del Lead che la barra mostra (`cardTitle` della scheda, o il nome della riga).
 */
export type StartCallInput = { tab: TabContext; leadId: string; phone: PhoneSlot; leadName: string; dialerSessionId?: string };

/**
 * L'esito confermato dal server; `late` se la chiamata ne aveva già un altro, che resta (`409 call_outcome_exists`). `call` è
 * la chiamata come la dà il server al salvataggio, con la durata della conversazione se la conosce già.
 */
export type OutcomeSaved = { late: boolean; call: CallView };

/** Ciò che la postazione ha scelto e scritto e non ha ancora salvato. */
export type OutcomeDraft = { outcome: OutcomeInput["outcome"] | null; answeredOutcome: OutcomeInput["answeredOutcome"] | null; note: string };

export type CallSessionApi = {
  session: CallSession;
  /**
   * Vero quando la fase è `outcome`, l'esito è confermato dal server e nessun passo obbligatorio dopo l'esito è da fare (li
   * dice la postazione con `setPendingSteps`, finché è nella pagina).
   */
  stepsDone: boolean;
  start(input: StartCallInput): Promise<void>;
  /** «Chiudi la chiamata» durante la chiamata: riaggancia e chiede l'esito (prima che il telefono componga: la abbandona). */
  hangUp(): void;
  /**
   * Salva l'esito; risolve quando il server lo conferma (`late`: la chiamata ne aveva già uno, che resta), rifiuta con il
   * rifiuto del server. Un guasto passeggero si riprova da solo, con la stessa chiave, finché la pagina è aperta. Una
   * chiamata finita senza arrivare al Lead (`409 invalid_state`: non ha esito) porta la sessione a `error`
   * (`not_connected`), così «Chiama» torna subito; così anche una chiamata che il server non trova più (`404 call_not_found`,
   * `call_gone`), che altrimenti terrebbe la postazione ferma fino al ricaricamento della pagina.
   */
  saveOutcome(input: OutcomeInput): Promise<OutcomeSaved>;
  /** Chiude la postazione e torna a `idle`; rifiutata (`false`) finché `stepsDone` è falso. */
  close(): boolean;
  /**
   * L'esito «Nessuna risposta» che Gioia365 ha già dato alla chiamata `callId` (TEL-32): la postazione lo prende come salvato, senza
   * mandarlo al server, e si chiude (`idle`). Soltanto nella fase `outcome` di quella chiamata e prima di ogni salvataggio
   * (`save.state === "none"`); `false` altrimenti, e non cambia niente.
   */
  acceptAutomaticOutcome(callId: string): boolean;
  /** «Manca l'esito…»: riprende una chiamata finita senza esito di chi guarda, se la sessione è libera (`idle`). */
  resume(input: { tab: TabContext; call: CallView; leadName: string }): void;
  /** I passi obbligatori dopo l'esito ancora da fare, come li calcola la postazione (`OutcomeSteps.tsx`, `pendingRequiredSteps`). */
  setPendingSteps(steps: readonly string[]): void;
  /**
   * Gli stessi passi, nell'ordine della postazione, come li ha detti l'ultima volta: restano anche quando la postazione non è
   * nella pagina, così la barra della chiamata (`CallBar.tsx`) dice che cosa manca davvero. Si svuotano con `close()`.
   */
  pendingSteps: readonly string[];
  /**
   * I passi dopo l'esito già fatti in questa chiamata (piano M5, Task 17: `callback`, il richiamo fissato), che la postazione
   * passa a `pendingRequiredSteps` come `done`. Stanno qui e non nella postazione, così restano fatti anche se la scheda si
   * lascia e si riapre prima di chiudere la chiamata; si svuotano quando la postazione si chiude o la chiamata non è partita.
   */
  doneSteps: readonly string[];
  /** Segna fatto un passo dopo l'esito della chiamata in corso (soltanto nella fase `outcome`). */
  markStepDone(step: string): void;
  /**
   * La bozza della postazione (esito scelto e appunti) non ancora salvata, per la chiamata `callId`: sta qui e non nella
   * postazione, così non si perde se la scheda si lascia e si riapre prima di salvare (rilievo di Codex sulla #90). `null` se non
   * ce n'è, o se è di un'altra chiamata; si svuota quando la postazione si chiude.
   */
  draftOf(callId: string): OutcomeDraft | null;
  /** Ricorda la bozza della postazione per la chiamata `callId` (soltanto nella fase `outcome` di quella chiamata). */
  keepDraft(callId: string, draft: OutcomeDraft): void;
  /** Toglie l'errore di una chiamata non partita. */
  dismiss(): void;
};

export const IDLE: CallSession = { phase: "idle" };

/** La chiamata è in corso: da «Chiama» premuto fino alla chiusura. */
export function isCallLive(session: CallSession): session is OngoingSession {
  return session.phase === "starting" || session.phase === "ringing" || session.phase === "live";
}

/** Una chiave di idempotenza del browser. */
export function newRequestId(): string {
  return crypto.randomUUID();
}
